import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Copy, Folder, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { apiClient } from '../../api';
import { useAuth } from '../../auth/AuthProvider';
import { useChat } from '../../contexts/ChatContext';
import { errMessage } from '../UI/ink';
import { parseNotes } from '../Findings/model';
import Markdown, { hrefFor } from './Markdown';

const mono = 'font-mono text-[10px] tracking-[0.12em]';
const SEV_TOKEN = { critical: 'crit', high: 'high', medium: 'med', low: 'low' };
const KIND_LABEL = { finding: 'FINDING', ioc: 'IOC', cve: 'CVE', asset: 'ASSET', case: 'CASE', run: 'RUN', action: 'ACTION', misp: 'INTEL' };
const clock = (iso) => {
  const d = iso ? new Date(String(iso).includes('T') ? iso : `${String(iso).replace(' ', 'T')}Z`) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
};
const secs = (ms) => (ms == null ? '' : `${(ms / 1000).toFixed(1)} s`);

// Proposal outcomes are remembered per browser so a reloaded chat doesn't offer the same change twice.
const readDone = () => { try { return JSON.parse(localStorage.getItem('assistant_proposals') || '{}'); } catch { return {}; } };
const writeDone = (map) => { try { localStorage.setItem('assistant_proposals', JSON.stringify(map)); } catch { /* ignore */ } };

export const SevChip = ({ severity }) => {
  const t = SEV_TOKEN[String(severity || '').toLowerCase()];
  if (!t) return null;
  return (
    <span className="inline-flex items-center gap-[5px] h-5 px-[7px] rounded-[5px] font-mono text-[10px] font-medium tracking-[0.06em] shrink-0" style={{ background: `var(--sev-${t}-bg)` }}>
      <span className="w-1.5 h-1.5 rounded-[1px]" style={{ background: `var(--sev-${t}-fg)` }} />{String(severity).toUpperCase()}
    </span>
  );
};

export const Trace = ({ steps, running, durationMs }) => {
  const [open, setOpen] = useState(false);
  if (!steps?.length && !running) return null;
  const n = steps?.length || 0;
  return (
    <div className="flex flex-col rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="h-9 flex items-center gap-2 px-3 text-[12.5px] text-left" style={{ color: 'var(--ink-muted)' }}>
        {running ? <Loader2 className="w-[13px] h-[13px] animate-spin" style={{ color: 'var(--accent)' }} /> : <Check className="w-[13px] h-[13px]" strokeWidth={2.6} style={{ color: 'var(--sev-good-fg)' }} />}
        <span className="flex-grow truncate">{running ? (steps.find(s => s.running) ? `Looking up ${steps.find(s => s.running).name}…` : n ? `Looked at ${n} thing${n === 1 ? '' : 's'} · thinking…` : 'Thinking…') : `Looked at ${n} thing${n === 1 ? '' : 's'}`}</span>
        {!running && durationMs != null && <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{secs(durationMs)}</span>}
        {n > 0 && <ChevronDown className={`w-[13px] h-[13px] transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--ink-faint)' }} />}
      </button>
      {open && n > 0 && (
        <div className="flex flex-col gap-[7px] px-3 pt-1 pb-[11px]">
          {steps.map((s, i) => (
            <span key={i} className="flex flex-col gap-0.5 min-w-0">
              <span className="flex gap-[7px] font-mono text-[11.5px] min-w-0"><span className="shrink-0">{s.name}</span><span className="truncate" style={{ color: 'var(--ink-faint)' }} title={s.args}>{s.args}</span></span>
              <span className="text-xs" style={{ color: s.error ? 'var(--sev-crit-fg)' : 'var(--ink-muted)' }}>{s.running ? '…' : `→ ${s.summary}`}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Related = ({ citations }) => {
  const [all, setAll] = useState(false);
  const list = (citations || []).filter(c => c.kind !== 'asset' || citations.length < 6);
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 4);
  return (
    <div className="flex flex-col gap-1.5">
      <span className={mono} style={{ color: 'var(--ink-faint)' }}>RELATED</span>
      {shown.map(c => {
        const href = hrefFor(c);
        const inner = (
          <>
            <span className="flex items-center gap-2 min-w-0">
              {c.severity ? <SevChip severity={c.severity} /> : <span className="h-5 inline-flex items-center px-[7px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em] shrink-0" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{KIND_LABEL[c.kind]}</span>}
              <span className="text-[13px] font-medium truncate">{c.label}</span>
            </span>
            <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{[c.kind === 'run' ? null : c.id, c.meta].filter(Boolean).join(' · ')}</span>
          </>
        );
        const cls = 'flex flex-col gap-[5px] px-3 py-2.5 rounded-[10px] border min-w-0';
        const style = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };
        return href
          ? <Link key={`${c.kind}:${c.id}`} to={href} className={`${cls} hover:border-[color:var(--surf-line-strong)]`} style={style}>{inner}</Link>
          : <div key={`${c.kind}:${c.id}`} className={cls} style={style}>{inner}</div>;
      })}
      {list.length > 4 && <button type="button" onClick={() => setAll(v => !v)} className="self-start text-[12.5px]" style={{ color: 'var(--ink-link)' }}>{all ? 'Show fewer' : `Show all ${list.length}`}</button>}
    </div>
  );
};

const Timeline = ({ events }) => {
  const [all, setAll] = useState(false);
  if (!events?.length) return null;
  const shown = all ? events : events.slice(-10);
  const sameDay = new Set(events.map(e => new Date(e.at).toDateString())).size === 1;
  const fmt = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return sameDay ? hm : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hm}`;
  };
  const dot = (k) => ({ finding: 'var(--sev-high-fg)', ioc: 'var(--sev-crit-fg)', cve: 'var(--sev-med-fg)', change: 'var(--ink-muted)', action: 'var(--accent)', run: 'var(--ink-muted)' }[k] || 'var(--ink-faint)');
  return (
    <div className="flex flex-col">
      <span className={`${mono} pb-1.5`} style={{ color: 'var(--ink-faint)' }}>TIMELINE{events.length > shown.length ? ` · LAST ${shown.length} OF ${events.length}` : ''}</span>
      {shown.map((e, i) => (
        <div key={i} className={`grid ${sameDay ? 'grid-cols-[42px_14px_minmax(0,1fr)]' : 'grid-cols-[84px_14px_minmax(0,1fr)]'} gap-2 items-start`}>
          <span className="font-mono text-[11.5px] leading-[18px]" style={{ color: 'var(--ink-faint)' }}>{fmt(e.at)}</span>
          <span className="flex flex-col items-center h-full min-h-[26px]">
            <span className="w-[7px] h-[7px] mt-[5px] rounded-full" style={{ background: dot(e.kind) }} />
            {i < shown.length - 1 && <span className="flex-grow w-px" style={{ background: 'var(--surf-line)' }} />}
          </span>
          <span className="text-[12.5px] leading-[18px] pb-2" style={{ color: 'var(--ink-muted)' }}><span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-text)' }}>{e.host}</span> · {e.event}</span>
        </div>
      ))}
      {events.length > 10 && <button type="button" onClick={() => setAll(v => !v)} className="self-start text-[12.5px]" style={{ color: 'var(--ink-link)' }}>{all ? 'Show last 10' : `Show all ${events.length}`}</button>}
    </div>
  );
};

const ProposalRow = ({ p, conversationId, onPinned }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(() => readDone()[p.id] || null); // null | 'done' | 'undone'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(false);
  const editable = p.type === 'finding_note' || p.type === 'case_note';
  const [text, setText] = useState(p.params.note || p.params.content || '');
  const mark = (s) => { const m = readDone(); m[p.id] = s; writeDone(m); setState(s); };

  const apply = async () => {
    setBusy(true); setError(null);
    const q = p.params;
    try {
      if (p.type === 'link_to_case') {
        for (const fid of q.finding_ids || []) await apiClient.post(`/api/cases/${q.case_id}/findings`, { finding_id: fid }).catch(e => { if (e?.response?.status !== 409) throw e; });
        for (const a of q.alerts || []) await apiClient.post(`/api/cases/${q.case_id}/alerts`, { alert_type: a.type, alert_id: a.id }).catch(e => { if (e?.response?.status !== 409) throw e; });
      } else if (p.type === 'finding_note') {
        const r = await apiClient.get(`/api/findings/${q.finding_id}`);
        const notes = parseNotes(r.data?.finding?.notes);
        const next = [...notes, { who: user?.name || user?.email || null, email: user?.email || null, at: new Date().toISOString(), text: text.trim() }];
        await apiClient.put(`/api/findings/${q.finding_id}`, { notes: JSON.stringify(next) });
      } else if (p.type === 'assign_finding') {
        await apiClient.put(`/api/findings/${q.finding_id}`, { assigned_to: q.assignee_email });
      } else if (p.type === 'case_note') {
        await apiClient.post(`/api/cases/${q.case_id}/notes`, { content: text.trim() });
        if (conversationId) { await apiClient.patch(`/api/assistant/conversations/${conversationId}`, { case_id: q.case_id }).catch(() => {}); onPinned?.(); }
      } else if (p.type === 'playbook_run') {
        const body = { target_context: q.context || undefined };
        if (q.target_type && q.target_id) { body.target_type = q.target_type; body.target_id = q.target_id; }
        const r = await apiClient.post(`/api/workflows/${q.workflow_id}/execute`, body);
        mark('done');
        if (r.data?.run_id) navigate(`/playbooks?run=${r.data.run_id}`);
        return;
      }
      mark('done');
      setReview(false);
    } catch (err) {
      setError(errMessage(err, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (p.type !== 'link_to_case') return;
    setBusy(true); setError(null);
    try {
      const q = p.params;
      for (const fid of q.finding_ids || []) await apiClient.delete(`/api/cases/${q.case_id}/findings/${fid}`);
      for (const a of q.alerts || []) await apiClient.delete(`/api/cases/${q.case_id}/alerts/${a.type}/${a.id}`);
      mark('undone');
    } catch (err) {
      setError(errMessage(err, 'Could not undo.'));
    } finally {
      setBusy(false);
    }
  };

  const LABEL = { link_to_case: 'Link', finding_note: 'Review note', assign_finding: 'Assign', playbook_run: 'Start run', case_note: 'Review and save' };
  const DONE = { link_to_case: 'Linked', finding_note: 'Note added', assign_finding: 'Assigned', playbook_run: 'Run started', case_note: 'Saved to case' };
  return (
    <div className="flex flex-col gap-2 py-2 pl-3 pr-2 rounded-[10px] border" style={{ borderColor: 'var(--surf-line-strong)' }}>
      <div className="flex items-center gap-2.5">
        <span className="flex-grow text-[12.5px] leading-snug min-w-0">{p.summary}{p.params.reason ? <span style={{ color: 'var(--ink-faint)' }}> · {p.params.reason}</span> : null}</span>
        {state === 'done' ? (
          <span className="shrink-0 flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
            <Check className="w-[13px] h-[13px]" strokeWidth={2.6} style={{ color: 'var(--sev-good-fg)' }} />{DONE[p.type]}
            {p.type === 'link_to_case' && <button type="button" disabled={busy} onClick={undo} className="h-[34px] px-1" style={{ color: 'var(--ink-link)' }}>Undo</button>}
          </span>
        ) : editable && !review ? (
          <button type="button" onClick={() => setReview(true)} className="h-[34px] shrink-0 px-3 rounded-lg border text-[13px] font-medium" style={{ borderColor: 'var(--surf-line-strong)' }}>{LABEL[p.type]}</button>
        ) : !editable ? (
          <button type="button" disabled={busy} onClick={apply} className="h-[34px] shrink-0 px-3 rounded-lg text-[13px] font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>{busy ? '…' : state === 'undone' ? 'Link again' : LABEL[p.type]}</button>
        ) : null}
      </div>
      {review && state !== 'done' && (
        <div className="flex flex-col gap-2">
          <label className="sr-only" htmlFor={`prop-${p.id}`}>Text to save</label>
          <textarea id={`prop-${p.id}`} rows={p.type === 'case_note' ? 10 : 4} value={text} onChange={e => setText(e.target.value)} className="box-border px-2.5 py-2 rounded-lg border text-[12.5px] leading-normal resize-y outline-none focus:border-[color:var(--accent)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }} />
          <span className="flex items-center gap-2">
            <button type="button" disabled={busy || !text.trim()} onClick={apply} className="h-[34px] px-3 rounded-lg text-[13px] font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>{busy ? 'Saving…' : p.type === 'case_note' ? `Save to ${p.params.case_title || 'case'}` : 'Add note'}</button>
            <button type="button" onClick={() => setReview(false)} className="h-[34px] px-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
          </span>
        </div>
      )}
      {error && <span role="alert" className="text-xs" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
    </div>
  );
};

const SaveToCase = ({ content, conversationId, onPinned }) => {
  const [open, setOpen] = useState(false);
  const [cases, setCases] = useState(null);
  const [state, setState] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    if (!cases) apiClient.get('/api/cases?limit=50').then(r => setCases((r.data?.cases || []).filter(c => c.status !== 'closed'))).catch(() => setCases([]));
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, cases]);
  const save = async (c) => {
    setState('saving');
    try {
      await apiClient.post(`/api/cases/${c.id}/notes`, { content });
      if (conversationId) { await apiClient.patch(`/api/assistant/conversations/${conversationId}`, { case_id: c.id }).catch(() => {}); onPinned?.(); }
      setState(`Saved to ${c.title}`);
      setOpen(false);
    } catch {
      setState('Could not save');
    }
  };
  return (
    <span ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="h-8 flex items-center gap-1.5 px-2 rounded-[7px] text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
        <Folder className="w-[13px] h-[13px]" strokeWidth={1.9} />{state && state !== 'saving' ? state : 'Save to case'}
      </button>
      {open && (
        <div role="menu" className="absolute left-0 bottom-9 z-20 w-[280px] max-h-[260px] overflow-y-auto p-1 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}>
          {cases === null && <span className="block px-3 py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Loading cases…</span>}
          {cases?.length === 0 && <span className="block px-3 py-2 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>No open cases.</span>}
          {cases?.map(c => (
            <button key={c.id} type="button" role="menuitem" onClick={() => save(c)} className="w-full flex flex-col gap-0.5 px-3 py-2 rounded-md text-left hover:bg-[color:var(--surf-raised)]">
              <span className="text-[13px] truncate">{c.title}</span>
              <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{c.status} · {c.priority}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
};

const AssistantMessage = ({ m, isLast, busy, onRegenerate, conversationId, onPinned, wide }) => {
  const [copied, setCopied] = useState(false);
  const meta = m.meta || {};
  const cites = meta.citations || [];
  const copy = async () => { try { await navigator.clipboard.writeText(m.content); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ } };
  return (
    <div className="flex flex-col gap-3">
      <span className="flex items-center gap-[7px] text-xs" style={{ color: 'var(--ink-faint)' }}>
        <Sparkles className="w-[13px] h-[13px]" strokeWidth={2} style={{ color: 'var(--accent)' }} />Assistant<span className="font-mono text-[10.5px]">· {clock(m.created_at)}</span>
      </span>
      <Trace steps={meta.trace} durationMs={meta.duration_ms} />
      <Markdown text={m.content} citations={cites} />
      <Related citations={cites} />
      <Timeline events={meta.timeline} />
      {meta.proposals?.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className={mono} style={{ color: 'var(--ink-faint)' }}>SUGGESTED NEXT STEPS · YOU CONFIRM EACH</span>
          {meta.proposals.map(p => <ProposalRow key={p.id} p={p} conversationId={conversationId} onPinned={onPinned} />)}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button type="button" onClick={copy} aria-label={copied ? 'Copied' : wide ? 'Copy as Markdown' : 'Copy answer'} className="h-8 flex items-center gap-1.5 px-2 rounded-[7px] text-[12.5px]" style={{ color: copied ? 'var(--sev-good-fg)' : 'var(--ink-faint)' }}>
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.9} />}{wide && (copied ? 'Copied' : 'Copy as Markdown')}
        </button>
        {isLast && <button type="button" disabled={busy} onClick={onRegenerate} aria-label="Regenerate" className="w-8 h-8 flex items-center justify-center rounded-[7px] disabled:opacity-40" style={{ color: 'var(--ink-faint)' }}><RefreshCw className="w-3.5 h-3.5" strokeWidth={1.9} /></button>}
        <SaveToCase content={m.content} conversationId={conversationId} onPinned={onPinned} />
        {cites.length > 0 && <span className="ml-auto font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{cites.length} record{cites.length === 1 ? '' : 's'} cited{meta.provider ? ` · ${meta.provider.model}` : ''}</span>}
      </div>
    </div>
  );
};

export default function Thread({ wide = false }) {
  const { messages, live, error, send, conversationId, loadConversation, stop } = useChat();
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, live?.steps?.length]);
  const lastAsst = messages.map(m => m.role).lastIndexOf('assistant');
  const onPinned = () => conversationId && loadConversation(conversationId);
  return (
    <div className="flex flex-col gap-4">
      {messages.map((m, i) => (m.role === 'user' ? (
        <div key={m.id} className="flex flex-col items-end gap-1">
          <div className={`${wide ? 'max-w-[460px]' : 'max-w-[330px]'} box-border px-[13px] py-2.5 rounded-[12px_12px_4px_12px] text-[13.5px] leading-normal whitespace-pre-wrap break-words`} style={{ background: 'var(--surf-raised)' }}>
            {m.content.startsWith('/') ? <><span className="font-mono text-[12.5px]" style={{ color: 'var(--ink-link)' }}>{m.content.split(' ')[0]}</span>{m.content.slice(m.content.split(' ')[0].length)}</> : m.content}
          </div>
          {m.meta?.context?.length > 0 && <span className="font-mono text-[10.5px] text-right" style={{ color: 'var(--ink-faint)' }}>with {m.meta.context.map(c => `${c.kind} ${c.label || c.id}`).join(', ')}</span>}
          <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>You · {clock(m.created_at)}</span>
        </div>
      ) : (
        <AssistantMessage key={m.id} m={m} isLast={i === lastAsst && !live} busy={!!live} onRegenerate={() => send('', { regenerate: true })} conversationId={conversationId} onPinned={onPinned} wide={wide} />
      )))}
      {live && (
        <div className="flex flex-col gap-3">
          <span className="flex items-center gap-[7px] text-xs" style={{ color: 'var(--ink-faint)' }}>
            <Sparkles className="w-[13px] h-[13px]" strokeWidth={2} style={{ color: 'var(--accent)' }} />Assistant
            <button type="button" onClick={stop} className="ml-auto h-7 px-2.5 rounded-md border text-xs" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Stop</button>
          </span>
          <Trace steps={live.steps} running />
        </div>
      )}
      {error && <div role="alert" className="px-3 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
      <div ref={endRef} />
    </div>
  );
}
